import { Fragment, useState } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon, SunIcon, MoonIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/20/solid'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useDarkMode } from '../context/DarkModeContext'
import { getCurrentUser } from '../services/authService'
import { clearSession } from '../services/sessionCookie'

function classNames(...classes) {
	return classes.filter(Boolean).join(' ')
}

export default function UserDropdown() {
	const navigate = useNavigate()
	const { isDarkMode, toggleDarkMode } = useDarkMode()
	const user = getCurrentUser()
	const userEmail = user?.email || localStorage.getItem('email') || ''
	const [isLoggingOut, setIsLoggingOut] = useState(false)

	const handleLogout = async () => {
		setIsLoggingOut(true)
		try {
			await axios.post('/api/user/logout-session')

			clearSession()
			localStorage.removeItem('email')

			window.location.href = '/'
		} catch (error) {
			console.log('error', error)
			clearSession()
			localStorage.removeItem('email')

			window.location.href = '/'
		}
	}

	const navigateToSettings = () => {
		navigate('/settings')
	}

	const handleContactUs = () => {
		if (window.$crisp) {
			window.$crisp.push(['do', 'chat:show'])
			window.$crisp.push(['do', 'chat:open'])
		}
	}

	return (
		<Menu as="div" className="relative inline-block text-left">
			{() => (
				<>
					<div>
						<Menu.Button className="inline-flex items-center gap-x-2 text-base font-medium text-sky-500 hover:text-sky-400 transition-colors">
							<div className="flex items-center justify-center w-7 h-7 rounded-full bg-sky-500 text-white text-xs font-semibold">
								{userEmail?.slice(0, 2)?.toUpperCase()}
							</div>
							<ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
						</Menu.Button>
					</div>

			<Transition
				as={Fragment}
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items
					static={isLoggingOut}
					className={classNames(
						'absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md shadow-lg ring-1 focus:outline-none',
						isDarkMode
							? 'bg-[#0D1117] ring-gray-800'
							: 'bg-white ring-gray-200'
					)}>
					<div className="py-1">
						<div
							className={classNames(
								'border-b px-4 py-2.5',
								isDarkMode ? 'border-gray-700' : 'border-gray-200'
							)}
						>
							<div
								className={classNames(
									'truncate whitespace-nowrap text-sm font-medium',
									'text-sky-500'
								)}
								title={userEmail}
							>
								{userEmail || 'Unknown account'}
							</div>
						</div>
						<Menu.Item>
							{({ active }) => (
								<button
									onClick={navigateToSettings}
									className={classNames(
										'flex w-full items-center px-4 py-2 text-sm',
										isDarkMode
											? active ? 'bg-gray-800 text-white' : 'text-gray-300'
											: active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
									)}
								>
									<Cog6ToothIcon className={classNames(
										'mr-3 h-5 w-5',
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									)} aria-hidden="true" />
									Settings
								</button>
							)}
						</Menu.Item>
						<Menu.Item>
							{({ active }) => (
								<button
									onClick={handleContactUs}
									className={classNames(
										'flex w-full items-center px-4 py-2 text-sm',
										isDarkMode
											? active ? 'bg-gray-800 text-white' : 'text-gray-300'
											: active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
									)}
								>
									<ChatBubbleLeftEllipsisIcon className={classNames(
										'mr-3 h-5 w-5',
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									)} aria-hidden="true" />
									Contact Us
								</button>
							)}
						</Menu.Item>
						<div className={classNames(
							'border-t my-1',
							isDarkMode ? 'border-gray-700' : 'border-gray-200'
						)}></div>
						<Menu.Item>
							{({ active }) => (
								<button
									onClick={toggleDarkMode}
									className={classNames(
										'flex w-full items-center px-4 py-2 text-sm',
										isDarkMode
											? active ? 'bg-gray-800 text-white' : 'text-gray-300'
											: active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
									)}
								>
									{isDarkMode ? (
										<>
											<SunIcon className={classNames(
												'mr-3 h-5 w-5',
												isDarkMode ? 'text-gray-400' : 'text-gray-500'
											)} aria-hidden="true" />
											Light Mode
										</>
									) : (
										<>
											<MoonIcon className={classNames(
												'mr-3 h-5 w-5',
												isDarkMode ? 'text-gray-400' : 'text-gray-500'
											)} aria-hidden="true" />
											Dark Mode
										</>
									)}
								</button>
							)}
						</Menu.Item>
						<div className={classNames(
							'border-t my-1',
							isDarkMode ? 'border-gray-700' : 'border-gray-200'
						)}></div>
						<Menu.Item>
							{({ active }) => (
								<button
									onClick={(e) => {
										e.preventDefault()
										handleLogout()
									}}
									disabled={isLoggingOut}
									className={classNames(
										'flex w-full items-center px-4 py-2 text-sm',
										isDarkMode
											? active ? 'bg-gray-800 text-white' : 'text-gray-300'
											: active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
										isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''
									)}
								>
									{isLoggingOut ? (
										<>
											<svg className="animate-spin mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Logging out...
										</>
									) : (
										<>
											<ArrowRightOnRectangleIcon className={classNames(
												'mr-3 h-5 w-5',
												isDarkMode ? 'text-gray-400' : 'text-gray-500'
											)} aria-hidden="true" />
											Log out
										</>
									)}
								</button>
							)}
						</Menu.Item>
					</div>
				</Menu.Items>
			</Transition>
				</>
			)}
		</Menu>
	)
}
